/* eslint-disable @typescript-eslint/only-throw-error -- TanStack Router's redirect() returns a non-Error object that must be thrown to trigger navigation */
import {
  redirect,
  Navigate,
  createRoute,
  createRouter,
  createRootRoute,
} from '@tanstack/react-router';
import App from '../App';
import InsightsPage from '@/pages/observe/insights';
import OrgSettingsPage from '../pages/config/org-settings';
import NotFoundPage from '../pages/not-found';
import AdminUsersPage from '@/pages/config/admin-users';
import UsersPage from '../pages/config/users';
import { AuthProvider } from '../lib/services/auth.service';
import PrivateModelsPage from '@/pages/wais/private-models';
import ContextProvidersPage from '@/pages/wais/datasources';
import GroupsPage from '@/pages/config/groups';
import LoginPage from '@/pages/login';
import ErrorPage from '@/pages/error';
import OrgPolicyPage from '@/pages/wais/org-policy';
import PoliciesPageV1 from '@/pages/wais/policies';
import PolicyViewPageV1 from '@/pages/wais/policies/view';
import PublicServicesPage from '@/pages/wais/public-services';
import UserEventsPage from '@/pages/observe/user-events';
import ConnectorsPage from '@/pages/config/connectors';
import LogoutPage from '@/pages/logout';
import AgentsPage from '@/pages/wais/agents';
import AgentViewPage from '@/pages/wais/agents/view';
import TelemetryPage from '@/pages/config/telemetry';
import ClassifiersPage from '@/pages/wais/classifiers';
import McpServersPage from '@/pages/wais/mcp-servers';
import AuditLogsPage from '@/pages/config/audit-logs';
import PoliciesPage from '@/pages/config/policy';
import PolicyViewPage from '@/pages/config/policy/view';

const rootRoute = createRootRoute({
  component: App,
});

/*
 * This check is technically a failsafe because of the authentication check that happens
 * in @/src/context/app/index.tsx. Read the comment in that file in the primary useEffect
 * hook for more details.
 */
const requireAuth = () => {
  if (!AuthProvider.user) {
    throw redirect({
      to: '/login',
    });
  }
};

const routes = [
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    component: () => <Navigate to="/observe/user-events" />,
  }),
  // sp-2301 handle deep links from metabase (e.g., /question/123 or ?question=123). for now we won't use them, but we might want to in the future
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/observe/insights',
    component: InsightsPage,
    beforeLoad: (ctx) => {
      requireAuth();
      const search = (ctx.search || {}) as Record<string, unknown>;
      if (typeof search.question !== 'undefined' || typeof search.dashboard !== 'undefined') {
        throw redirect({ to: '/observe/insights' });
      }
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/observe/insights/question/$questionId',
    beforeLoad: ({ search }) => {
      throw redirect({
        to: '/observe/insights',
        search,
      });
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/observe/insights/dashboard/$dashboardId',
    beforeLoad: ({ search }) => {
      throw redirect({
        to: '/observe/insights',
        search,
      });
    },
  }),
  // @todo old insights route. remove after ensuring no recent usage
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/insights',
    component: () => <Navigate to="/observe/insights" />,
    beforeLoad: requireAuth,
  }),
  // @todo old insights route. remove after ensuring no recent usage
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/home',
    component: () => <Navigate to="/observe/insights" />,
    beforeLoad: requireAuth,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/wais/public-services',
    component: PublicServicesPage,
    beforeLoad: requireAuth,
  }),
  // old public services route
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/config/public-services',
    component: () => <Navigate to="/wais/public-services" />,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/wais/mcp-servers',
    component: McpServersPage,
    beforeLoad: requireAuth,
  }),
  // old mcp servers route
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/config/mcp-servers',
    component: () => <Navigate to="/wais/mcp-servers" />,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/wais/private-models',
    component: PrivateModelsPage,
    beforeLoad: requireAuth,
  }),
  // old private models route
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/config/private-models',
    component: () => <Navigate to="/wais/private-models" />,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/config/context-sources',
    component: () => <Navigate to="/wais/datasources" />,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/wais/datasources',
    component: ContextProvidersPage,
    beforeLoad: requireAuth,
  }),
  // old data sources route
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/config/data-sources',
    component: () => <Navigate to="/wais/datasources" />,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/wais/assistants',
    component: AgentsPage,
    beforeLoad: requireAuth,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/wais/assistants/$agentId',
    component: AgentViewPage,
    beforeLoad: requireAuth,
  }),
  // old assistants routes
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/config/assistants',
    component: () => <Navigate to="/wais/assistants" />,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/config/assistants/$agentId',
    beforeLoad: ({ params }) => {
      throw redirect({
        to: '/wais/assistants/$agentId',
        params,
      });
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/wais/classifiers',
    component: ClassifiersPage,
    beforeLoad: requireAuth,
  }),
  // old classifiers route
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/config/classifiers',
    component: () => <Navigate to="/wais/classifiers" />,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/config/intents',
    component: ClassifiersPage,
    beforeLoad: requireAuth,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/config/telemetry-destinations',
    component: TelemetryPage,
    beforeLoad: requireAuth,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/config/audit-logs',
    component: AuditLogsPage,
    beforeLoad: requireAuth,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/observe/user-events',
    component: UserEventsPage,
    beforeLoad: requireAuth,
  }),
  // old user events route
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/user-events',
    beforeLoad: ({ search }) => {
      throw redirect({
        to: '/observe/user-events',
        search,
      });
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/config/org-settings',
    component: OrgSettingsPage,
    beforeLoad: requireAuth,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/config/groups',
    component: GroupsPage,
    beforeLoad: requireAuth,
  }),

  // Policy V1 Routes
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/wais/group-policies',
    component: PoliciesPageV1,
    beforeLoad: requireAuth,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/wais/default-policy',
    component: OrgPolicyPage,
    beforeLoad: requireAuth,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/wais/group-policies/$policyId',
    component: PolicyViewPageV1,
    beforeLoad: requireAuth,
  }),
  // old policy v1 routes
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/config/group-policies',
    beforeLoad: ({ search, location }) => {
      throw redirect({
        to: '/wais/group-policies',
        search,
        hash: location.hash,
      });
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/config/default-policy',
    beforeLoad: ({ search, location }) => {
      throw redirect({
        to: '/wais/default-policy',
        search,
        hash: location.hash,
      });
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/config/group-policies/$policyId',
    beforeLoad: ({ params, search, location }) => {
      throw redirect({
        to: '/wais/group-policies/$policyId',
        params,
        search,
        hash: location.hash,
      });
    },
  }),

  // Policy V2 Routes
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/config/policy/scoped',
    component: PoliciesPage,
    beforeLoad: requireAuth,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/config/policy/default',
    component: PolicyViewPage,
    beforeLoad: requireAuth,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/config/policy/override',
    component: PolicyViewPage,
    beforeLoad: requireAuth,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/config/policy/scoped/$policyId',
    component: PolicyViewPage,
    beforeLoad: requireAuth,
  }),

  createRoute({
    getParentRoute: () => rootRoute,
    path: '/config/admin-users',
    component: AdminUsersPage,
    beforeLoad: requireAuth,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/config/end-users',
    component: UsersPage,
    beforeLoad: requireAuth,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/login',
    component: LoginPage,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/logout',
    component: LogoutPage,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/config/connectors',
    component: ConnectorsPage,
    beforeLoad: requireAuth,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/error',
    component: ErrorPage,
    beforeLoad: () => ({ layout: 'public' }),
    validateSearch: ({ code }: Record<string, unknown>): { code?: number } => {
      return {
        code: code ? Number(code) : undefined,
      };
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '*',
    component: NotFoundPage,
  }),
];

const routeTree = rootRoute.addChildren(routes);

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export default router;
